import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import { useDebounceCallback } from 'usehooks-ts';

import type { IApiGetArticleResponseBody } from '~/lib/api/modules/Articles';
import { UpdateArticleForm } from '~/components/forms/ArticleForm';
import { useAppPreviewDispatch } from '~/components/organisms/AppPreview';
import { CategoryBreadcrumbs } from '~/components/organisms/Breadcrumbs';
import { useNavigate } from '~/components/utils/Navigation';

const MIN_WIDTH_FOR_PREVIEW = 1600;

export type IAppUpdateArticlePageProps = {
  appId: string;
  articleId: string;
};

const DEBOUNCE_DELAY = 300;

const INITIAL_ARTICLE = {
  _id: '__fake_id',
  title: '',
  categories: [],
};

export const AppUpdateArticlePage: React.FC<IAppUpdateArticlePageProps> = (
  props,
) => {
  const { appId, articleId } = props;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const appPreviewDispatch = useAppPreviewDispatch();
  const [article, setArticle] = useState<IApiGetArticleResponseBody>({
    ...INITIAL_ARTICLE,
    appId,
  });

  // TODO: create a hook to auto open the preview
  useEffect(() => {
    const windowWidth = window.innerWidth;
    if (windowWidth > MIN_WIDTH_FOR_PREVIEW) {
      appPreviewDispatch.saveState();
      appPreviewDispatch.setOpened(true, { maximize: true });
    }

    return () => appPreviewDispatch.restoreState();
  }, [appPreviewDispatch]);

  useEffect(() => {
    if (article.isWebview && article.text) {
      appPreviewDispatch.navigateToUrl(article.text);
    } else {
      appPreviewDispatch.navigateToRoute({
        appId,
        path: '/articlePreview',
        tab: 'categories',
        navigateOptions: {
          props: { article },
          animate: false,
        },
      });
    }
  }, [appId, appPreviewDispatch, article]);

  const setArticleDebounced = useDebounceCallback(setArticle, DEBOUNCE_DELAY);

  const parentCategoryIdFromUrl = searchParams.get('parent');
  const mainParentCategoryId = article.categories?.[0]?._id;
  const parentCategoryId =
    (parentCategoryIdFromUrl
      ? article.categories?.find(
          (category) => category._id === parentCategoryIdFromUrl,
        )?._id
      : undefined) ?? mainParentCategoryId;

  const pageTitle = article.title;

  return (
    <>
      {pageTitle ? (
        <Helmet>
          <title>{pageTitle}</title>
        </Helmet>
      ) : null}

      {parentCategoryId && (
        <CategoryBreadcrumbs
          appId={appId}
          categoryId={parentCategoryId}
          includeSelf
        />
      )}

      <UpdateArticleForm
        appId={appId}
        articleId={articleId}
        onArticleChange={setArticleDebounced}
        onSuccess={(_, values) =>
          values.categoryIds?.length
            ? navigate(`/apps/${appId}/categories/${values.categoryIds[0]}`)
            : undefined
        }
        // TODO:
        // cancelUrl={
        //   data.categoriesId?.length
        //     ? `/apps/${appId}/categories/${data.categoriesId[0]}`
        //     : `/apps/${appId}/categories`
        // }
      />
    </>
  );
};
